"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Calendar, Search, ArrowRight, BookOpen } from "lucide-react"
import SEO from "../SEO"

const POSTS_PER_PAGE = 9
const BASE_URL = "/wp-json/wp/v2/blogs"

export default function BlogsPage() {

  /* ================= SEO ================= */

  const blogsPageSchema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaamhospitals.com/",
      "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
      "description":
        "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad providing compassionate healthcare and authoritative medical content through its blog.",
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Pranaam Hospitals Blog",
      "url": "https://pranaamhospitals.com/blogs",
      "description":
        "Browse the latest health and medical blog articles from Pranaam Hospitals covering wellness, disease prevention, treatment tips, and expert health advice.",
    },
  ]

  /* ================= STATE ================= */

  const [blogs, setBlogs] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");
  const hasFetchedInitially = useRef(false)

  /* ================= FETCH FUNCTION ================= */

  const fetchBlogs = async (pageNumber) => {
    if (loading || !hasMore) return

    setLoading(true)

    try {
      const res = await fetch(
        `${BASE_URL}?per_page=${POSTS_PER_PAGE}&page=${pageNumber}&_embed`
      )

      const totalPages = Number(res.headers.get("X-WP-TotalPages"))
      const data = await res.json()

      const mappedBlogs = data.map((item) => ({
        id: item.id,
        title: item.title.rendered,
        slug: item.slug,
        date: new Date(item.date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        }),
        excerpt:
          item.excerpt?.rendered?.replace(/<[^>]+>/g, "") ||
          "Read more about this article.",
        featuredImage:
          item._embedded?.["wp:featuredmedia"]?.[0]?.source_url || null,
      }))

      setBlogs((prev) => [...prev, ...mappedBlogs])

      if (pageNumber >= totalPages) {
        setHasMore(false)
      }

    } catch (error) {
      console.error("Error fetching blogs:", error)
      setHasMore(false)
    }

    setLoading(false)
    setInitialLoading(false)
  }

  /* ================= INITIAL LOAD (RUN ONLY ONCE) ================= */

  useEffect(() => {
    if (hasFetchedInitially.current) return
    hasFetchedInitially.current = true

    fetchBlogs(1)

    fetch("/wp-json/custom/v1/page-seo?slug=blogs")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {})
  }, [])

  /* ================= LOAD MORE ================= */

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchBlogs(nextPage)
  }

  /* ================= FILTER ================= */

  const filteredBlogs = blogs.filter((blog) =>
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  )

  /* ================= INITIAL LOADER ================= */

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  /* ================= RENDER ================= */

  return (
    <>
      <SEO
        title={seo.title || "Health & Medical Blogs | Pranaam Hospitals Hyderabad"}
        description={seo.description || "Read informative health & medical blog articles from Pranaam Hospitals covering wellness, preventive care, treatment tips, and expert health advice."}
        canonical={seo.canonical || "https://pranaamhospitals.com/blogs"}
        robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
        keywords={pageKeywords}
        ogTitle={seo.og_title}
        ogDescription={seo.og_description}
        ogImage={seo.og_image || seo.featured_image}
        twitterTitle={seo.twitter_title}
        twitterDescription={seo.twitter_description}
        author="Pranaam Hospitals"
        publisher="Pranaam Hospitals"
        schema={blogsPageSchema}
      />

      <div className="min-h-screen bg-white">
        <main>

          {/* Hero */}
          <section className="bg-gradient-to-r from-primary/10 to-primary/5 py-16 text-center">
            <Badge className="mb-4 px-3 py-1">Health & Wellness</Badge>
            <h1 className="text-4xl font-bold md:text-5xl">
              Pranaam <span className="text-primary">Health Blog</span>
            </h1>
          </section>

          {/* Search */}
          <section className="py-8 bg-gray-100">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 bg-white"
                />
              </div>
            </div>
          </section>

          {/* Blogs Grid */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-6xl">
               <h2 className="sr-only">
                  Latest Health Blog Articles
                </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

                {filteredBlogs.map((blog) => (
                  <Card key={blog.id} className="hover:shadow-xl">
                    <CardContent className="p-0">
                      <div className="relative h-72 bg-gray-100">
                        {blog.featuredImage ? (
                          <img
                            src={blog.featuredImage}
                            alt={blog.title}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <BookOpen className="w-16 h-16 text-primary/20" />
                          </div>
                        )}
                      </div>

                      <div className="p-6">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <Calendar className="w-4 h-4 text-primary" />
                          {blog.date}
                        </div>

                        <h3 className="font-bold mb-3 line-clamp-2">
                          {blog.title}
                        </h3>

                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                          {blog.excerpt}
                        </p>

                        <Link
                          to={`/blogs/${blog.slug}`}
                          className="inline-flex items-center text-primary font-medium hover:underline"
                          aria-label={`Read more about ${blog.title}`}
                        >
                          Read More About Blog
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Skeleton Loader */}
                {loading &&
                  [...Array(3)].map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="h-80 bg-gray-200 rounded-lg animate-pulse"
                    />
                  ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="text-center mt-12">
                  <Button
                    size="lg"
                    variant="outline"
                    disabled={loading}
                    onClick={handleLoadMore}
                    className="rounded-full border-primary text-primary"
                  >
                    {loading ? "Loading..." : "Load More Articles"}
                  </Button>
                </div>
              )}

            </div>
          </section>

        </main>
      </div>
    </>
  )
}